import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { getHealingRequests, getHealers, assignHealer, getTransferLog, reopenCase, returnToHealer, dropPatient, requestSessionFeedback, recallSessionFeedback, createHealingRequest, getUsers, addCaseNote, setFollowUp, sendHealingReport, earlyComplete } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';
import ActivityPanel from '../components/ActivityPanel';
import { FaWhatsapp } from 'react-icons/fa';
 
const HealingRequests = () => {
  const { isMobile } = useWindowSize();
  const [requests, setRequests] = useState([]);
  const [healers, setHealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [selectedHealer, setSelectedHealer] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [sessionsPlanned, setSessionsPlanned] = useState('');
  const [sessionRemarks, setSessionRemarks] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [sessionCountOption, setSessionCountOption] = useState('carry_over');
  const [newSessionsCount, setNewSessionsCount] = useState('');
  const [transferLogs, setTransferLogs] = useState({});
  const [showHealerName, setShowHealerName] = useState(false);
  const [dropModal, setDropModal] = useState(null);
  const [dropping, setDropping] = useState(false);
  const [earlyCompleteModal, setEarlyCompleteModal] = useState(null);
  const [earlyCompleteReason, setEarlyCompleteReason] = useState('');
  const [submittingEarlyComplete, setSubmittingEarlyComplete] = useState(false);

  const emptyNewReq = { patientMode: 'existing', selectedUserId: '', selectedUserName: '', manualPatientName: '', userSearch: '', userSearchResults: [], isForSelf: true, beneficiaryName: '', beneficiaryAge: '', beneficiaryRelationship: 'spouse', beneficiaryRelationshipOther: '', condition: '', description: '', urgency: 'normal', medicalReportsShared: false, medicalReportsNote: '' };
  const [newRequestModal, setNewRequestModal] = useState(false);
  const [newReqForm, setNewReqForm] = useState(emptyNewReq);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [submittingNewReq, setSubmittingNewReq] = useState(false);
 
  useEffect(() => {
    fetchData();
  }, []);
 
  const fetchData = async () => {
    try {
      const [reqRes, healerRes] = await Promise.all([
        getHealingRequests(),
        getHealers()
      ]);
      setRequests(reqRes.data.requests);
      setHealers(healerRes.data.healers);
    } catch (error) {
      console.log('Error:', error);
    } finally {
      setLoading(false);
    }
  };
 
  const isReassignment = assignModal && assignModal.assignedHealer && assignModal.status !== 'pending';

  const handleAssign = async () => {
    if (!selectedHealer) {
      alert('Please select a healer.');
      return;
    }
    if (!sessionsPlanned || parseInt(sessionsPlanned) < 1) {
      alert('Please enter total sessions planned (minimum 1).');
      return;
    }
    if (isReassignment && !transferReason) {
      alert('Reassignment reason is required.');
      return;
    }
    try {
      var data = {
        healerId: selectedHealer,
        adminNotes: adminNotes,
        sessions_planned: parseInt(sessionsPlanned),
        session_remarks: sessionRemarks,
        showHealerNameToPatient: showHealerName
      };
      if (isReassignment) {
        data.transfer_reason = transferReason;
        data.session_count_option = sessionCountOption;
        if (sessionCountOption === 'set_new') {
          data.new_sessions_count = parseInt(newSessionsCount);
        }
      }
      await assignHealer(assignModal._id, data);
      alert(isReassignment ? 'Healer reassigned successfully.' : 'Healer assigned successfully.');
      resetAssignModal();
      fetchData();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error assigning healer');
    }
  };

  const resetAssignModal = () => {
    setAssignModal(null);
    setSelectedHealer('');
    setAdminNotes('');
    setSessionsPlanned('');
    setSessionRemarks('');
    setTransferReason('');
    setSessionCountOption('carry_over');
    setNewSessionsCount('');
    setShowHealerName(false);
  };

  const handleReopen = async (requestId) => {
    var additional = prompt('How many additional sessions to add? (e.g. enter 2 to add 2 more sessions):');
    try {
      var data = {};
      if (additional && parseInt(additional) > 0) {
        data.additional_sessions = parseInt(additional);
      }
      await reopenCase(requestId, data);
      alert('Case reopened successfully.');
      fetchData();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error reopening case');
    }
  };

  const fetchTransferLog = async (requestId) => {
    try {
      var res = await getTransferLog(requestId);
      setTransferLogs(function(prev) { var copy = Object.assign({}, prev); copy[requestId] = res.data.transferLogs; return copy; });
    } catch (error) {
      console.log('Error fetching transfer log:', error);
    }
  };
 
  const [returnToHealerModal, setReturnToHealerModal] = useState(null);
  const [returnToHealerNote, setReturnToHealerNote] = useState('');
  const [returnToHealerShowName, setReturnToHealerShowName] = useState(false);

  // Bulk actions state (#4)
  const [selectedCases, setSelectedCases] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkAssignModal, setBulkAssignModal] = useState(false);
  const [bulkHealer, setBulkHealer] = useState('');
  const [bulkSessions, setBulkSessions] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);

  // Case notes state (#8)
  const [notesOpen, setNotesOpen] = useState(null); // requestId
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [caseNotesMap, setCaseNotesMap] = useState({}); // { requestId: [notes] }

  // Activities panel state
  const [activitiesOpen, setActivitiesOpen] = useState(null); // requestId
  const [activitiesMap, setActivitiesMap] = useState({}); // { requestId: [activities] }
  const [followUpOpen, setFollowUpOpen] = useState(null); // requestId
  const [followUpForm, setFollowUpForm] = useState({ date: '', note: '' });
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [reportModal, setReportModal] = useState(null); // healing request object
  const [reportForm, setReportForm] = useState({ recipientSearch: '', recipientEmail: '', recipientName: '', includeNotes: false });
  const [reportUserResults, setReportUserResults] = useState([]);
  const [sendingReport, setSendingReport] = useState(false);

  // Transfer log panel state (#19)
  const [transferPanelOpen, setTransferPanelOpen] = useState(null); // requestId
  const [expandedScanSessions, setExpandedScanSessions] = useState({});

  const handleReturnToSameHealer = async () => {
    try {
      await returnToHealer(returnToHealerModal._id, {
        adminNotes: returnToHealerNote || '',
        showHealerNameToPatient: returnToHealerShowName
      });
      alert('Patient returned to the same healer.');
      setReturnToHealerModal(null);
      setReturnToHealerNote('');
      setReturnToHealerShowName(false);
      fetchData();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error returning to healer');
    }
  };

  const handleEarlyComplete = async () => {
    if (!earlyCompleteReason.trim()) { alert('Please enter a reason for early closure.'); return; }
    setSubmittingEarlyComplete(true);
    try {
      await earlyComplete(earlyCompleteModal._id, { reason: earlyCompleteReason });
      alert('Case closed early successfully. Completion email sent to patient.');
      setEarlyCompleteModal(null);
      setEarlyCompleteReason('');
      fetchData();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error closing case early');
    } finally {
      setSubmittingEarlyComplete(false);
    }
  };

  const handleDropPatient = async () => {
    setDropping(true);
    try {
      var res = await dropPatient(dropModal._id);
      if (res.data.emailSent) {
        alert('Patient dropped successfully. Email notification sent.');
      } else {
        alert('Patient dropped. WARNING: Email failed to send — ' + (res.data.emailError || 'unknown error'));
      }
      setDropModal(null);
      fetchData();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error dropping patient');
    } finally {
      setDropping(false);
    }
  };

  var handleUserSearch = async function(query) {
    setNewReqForm(function(prev) { return Object.assign({}, prev, { userSearch: query, selectedUserId: '', selectedUserName: '' }); });
    if (!query || query.length < 2) {
      setNewReqForm(function(prev) { return Object.assign({}, prev, { userSearchResults: [] }); });
      return;
    }
    setUserSearchLoading(true);
    try {
      var res = await getUsers({ search: query });
      setNewReqForm(function(prev) { return Object.assign({}, prev, { userSearchResults: res.data.users || [] }); });
    } catch (e) {
      setNewReqForm(function(prev) { return Object.assign({}, prev, { userSearchResults: [] }); });
    } finally {
      setUserSearchLoading(false);
    }
  };

  var handleCreateRequest = async function() {
    if (newReqForm.patientMode === 'existing' && !newReqForm.selectedUserId) {
      alert('Please select an existing user.');
      return;
    }
    if (newReqForm.patientMode === 'manual' && !newReqForm.manualPatientName.trim()) {
      alert('Please enter the patient name.');
      return;
    }
    if (!newReqForm.condition.trim()) { alert('Please enter the condition.'); return; }
    if (!newReqForm.description.trim()) { alert('Please enter a description.'); return; }
    if (!newReqForm.isForSelf && !newReqForm.beneficiaryName.trim()) { alert('Please enter the beneficiary name.'); return; }
    setSubmittingNewReq(true);
    try {
      var payload = {
        condition: newReqForm.condition,
        description: newReqForm.description,
        urgency: newReqForm.urgency,
        isForSelf: newReqForm.isForSelf,
        beneficiaryName: newReqForm.beneficiaryName,
        beneficiaryAge: newReqForm.beneficiaryAge || null,
        beneficiaryRelationship: newReqForm.beneficiaryRelationship,
        beneficiaryRelationshipOther: newReqForm.beneficiaryRelationshipOther,
        medicalReportsShared: newReqForm.medicalReportsShared,
        medicalReportsNote: newReqForm.medicalReportsNote
      };
      if (newReqForm.patientMode === 'existing') {
        payload.patientId = newReqForm.selectedUserId;
      } else {
        payload.manualPatientName = newReqForm.manualPatientName.trim();
      }
      await createHealingRequest(payload);
      alert('Healing request created successfully.');
      setNewRequestModal(false);
      setNewReqForm(emptyNewReq);
      fetchData();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error creating request');
    } finally {
      setSubmittingNewReq(false);
    }
  };

  var handleSaveFollowUp = async function(reqId) {
    setSavingFollowUp(true);
    try {
      await setFollowUp(reqId, { followUpDate: followUpForm.date || null, followUpNote: followUpForm.note });
      setRequests(function(prev) {
        return prev.map(function(r) {
          return r._id === reqId ? Object.assign({}, r, { followUpDate: followUpForm.date || null, followUpNote: followUpForm.note }) : r;
        });
      });
      setFollowUpOpen(null);
    } catch(e) {
      alert('Could not save follow-up');
    } finally {
      setSavingFollowUp(false);
    }
  };

  var handleClearFollowUp = async function(reqId) {
    try {
      await setFollowUp(reqId, { followUpDate: null, followUpNote: '' });
      setRequests(function(prev) {
        return prev.map(function(r) {
          return r._id === reqId ? Object.assign({}, r, { followUpDate: null, followUpNote: '' }) : r;
        });
      });
    } catch(e) {
      alert('Could not clear follow-up');
    }
  };

  var handleRequestSessionFeedback = async function(reqId, sessionIndex, sessionDate, patientName) {
    if (!window.confirm('Request feedback from ' + patientName + ' for session on ' + sessionDate + '? An email and notification will be sent.')) return;
    try {
      var res = await requestSessionFeedback(reqId, sessionIndex);
      if (res.data.emailSent) {
        alert('Feedback request sent. Email + notification delivered to patient.');
      } else {
        alert('Feedback requested (notification sent). Email failed: ' + (res.data.emailError || 'unknown'));
      }
      fetchData();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error requesting feedback');
    }
  };

  var handleRecallSessionFeedback = async function(reqId, sessionIndex, sessionDate) {
    if (!window.confirm('Recall the pending feedback request for session on ' + sessionDate + '? The patient will no longer be prompted.')) return;
    try {
      await recallSessionFeedback(reqId, sessionIndex);
      fetchData();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error recalling feedback');
    }
  };

  var handleAddNote = async function(requestId) {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      var res = await addCaseNote(requestId, { text: noteText.trim() });
      setCaseNotesMap(function(prev) { return Object.assign({}, prev, { [requestId]: res.data.caseNotes }); });
      setNoteText('');
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error saving note');
    } finally {
      setSavingNote(false);
    }
  };

  var exportCSV = function() {
    var rows = [['Patient Name', 'Email', 'Phone', 'Condition', 'Status', 'Urgency', 'Healer', 'Sessions Planned', 'Sessions Done', 'Amount Paid', 'Requested On']];
    requests.forEach(function(r) {
      rows.push([
        r.patient ? r.patient.firstName + ' ' + r.patient.lastName : (r.manualPatientName || 'Unknown'),
        r.patient ? (r.patient.email || '') : '',
        r.patient ? (r.patient.phone || '') : '',
        r.condition || '',
        r.status || '',
        r.urgency || '',
        r.assignedHealer ? r.assignedHealer.firstName + ' ' + r.assignedHealer.lastName : 'Unassigned',
        r.sessions_planned || 0,
        r.totalSessionsCompleted || 0,
        r.payment && r.payment.amountPaid ? r.payment.amountPaid : 0,
        r.requestedAt ? new Date(r.requestedAt).toLocaleDateString('en-IN') : ''
      ]);
    });
    var csv = rows.map(function(row) {
      return row.map(function(cell) { return '"' + String(cell).replace(/"/g, '""') + '"'; }).join(',');
    }).join('\n');
    var blob = new Blob([csv], { type: 'text/csv' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = 'healing-requests-' + new Date().toISOString().split('T')[0] + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  var handleBulkAssign = async function() {
    if (!bulkHealer) { alert('Please select a healer.'); return; }
    if (!bulkSessions || parseInt(bulkSessions) < 1) { alert('Please enter sessions planned.'); return; }
    setBulkAssigning(true);
    var ids = Array.from(selectedCases);
    var errors = [];
    for (var i = 0; i < ids.length; i++) {
      try {
        await assignHealer(ids[i], { healerId: bulkHealer, sessions_planned: parseInt(bulkSessions) });
      } catch (e) {
        errors.push(ids[i]);
      }
    }
    setBulkAssigning(false);
    setBulkAssignModal(false);
    setBulkHealer('');
    setBulkSessions('');
    setSelectedCases(new Set());
    setBulkMode(false);
    fetchData();
    if (errors.length > 0) {
      alert(errors.length + ' case(s) failed to assign. The rest succeeded.');
    } else {
      alert(ids.length + ' case(s) assigned successfully.');
    }
  };

  var getDaysSinceActivity = function(req) {
    if (req.status !== 'assigned' && req.status !== 'in_progress') return null;
    var sessions = (req.sessions || []).filter(function(s) { return s.status === 'completed'; });
    var lastDate = sessions.length > 0
      ? new Date(Math.max.apply(null, sessions.map(function(s) { return new Date(s.date); })))
      : req.assignedAt ? new Date(req.assignedAt) : null;
    if (!lastDate) return null;
    return Math.floor((new Date() - lastDate) / (1000 * 60 * 60 * 24));
  };

  const pending = requests.filter(function(r) { return r.status === 'pending' && r.assignment_status !== 'returned'; });
  const returned = requests.filter(function(r) { return r.assignment_status === 'returned'; });
  const active = requests.filter(function(r) { return r.status === 'assigned' || r.status === 'in_progress'; });
  const completed = requests.filter(function(r) { return r.status === 'completed'; });

  const reasonCodeColors = {
    no_show: { bg: '#fdf0f0', color: '#e74c3c' },
    cancelled: { bg: '#fef9e7', color: '#d4ac0d' },
    unresponsive: { bg: '#fdf0f0', color: '#922B21' },
    other: { bg: '#f0f0f0', color: '#666' }
  };

  const escalated = active.filter(function(r) { var d = getDaysSinceActivity(r); return d !== null && d >= 14; });
  const displayList = activeTab === 'pending' ? pending : activeTab === 'active' ? active : activeTab === 'returned' ? returned : completed;

  var s = Object.assign({}, styles, isMobile ? {
    header: { ...styles.header, flexDirection: 'column', gap: '16px' },
    tabs: { ...styles.tabs, overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' },
    modal: { ...styles.modal, alignItems: 'flex-end', padding: '0' },
    modalCard: { ...styles.modalCard, borderRadius: '16px 16px 0 0', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '24px 20px' },
    requestHeader: { ...styles.requestHeader, flexDirection: 'column', gap: '12px' },
    requestHeaderLeft: { ...styles.requestHeaderLeft, flexDirection: 'column' },
    infoRow: { ...styles.infoRow, flexDirection: 'column' },
    actionRow: { ...styles.actionRow, flexWrap: 'wrap' },
    sessionSummaryRow: { ...styles.sessionSummaryRow, gridTemplateColumns: 'repeat(2, 1fr)' },
    sessionItem: { ...styles.sessionItem, gridTemplateColumns: '1fr' },
    modalBtns: { ...styles.modalBtns, flexDirection: 'column' },
    submitBtn: { ...styles.submitBtn, width: '100%' },
    cancelBtn: { ...styles.cancelBtn, width: '100%' },
    headerStats: { ...styles.headerStats, flexWrap: 'wrap', gap: '16px' }
  } : {});

  const statusColors = {
    pending: { bg: '#fef9e7', color: '#d4ac0d' },
    assigned: { bg: '#eaf4fb', color: '#2980b9' },
    in_progress: { bg: '#eafaf1', color: '#27ae60' },
    completed: { bg: '#f0f0f0', color: '#666' }
  };
 
  const urgencyColors = {
    normal: { bg: '#eafaf1', color: '#27ae60' },
    urgent: { bg: '#fef9e7', color: '#d4ac0d' },
    critical: { bg: '#fdf0f0', color: '#e74c3c' }
  };
 
  return (
    <div style={s.layout}>
      <Sidebar />
      <div style={{ ...s.main, marginLeft: isMobile ? 0 : '260px', padding: isMobile ? '56px 16px 16px' : '32px', minWidth: 0, overflowX: 'hidden' }}>
 
        {/* Header */}
        <div style={s.header}>
          <div>
            <h1 style={s.headerTitle}>Healing Requests</h1>
            <p style={s.headerSub}>Manage patient healing requests and healer assignments</p>
          </div>
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <button onClick={exportCSV} style={{ background: 'transparent', color: '#4a8f82', border: '1px solid #4a8f82', borderRadius: '6px', padding: '10px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' }}>Export CSV</button>
              <button onClick={function() { setBulkMode(!bulkMode); setSelectedCases(new Set()); }} style={{ background: bulkMode ? '#1a1a2e' : 'transparent', color: bulkMode ? '#fff' : '#666', border: '1px solid #ccc', borderRadius: '6px', padding: '10px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' }}>{bulkMode ? 'Exit Bulk' : 'Bulk Select'}</button>
              <button onClick={function() { setNewRequestModal(true); }} style={{ background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 22px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' }}>+ New Request</button>
              <div style={s.headerStats}>
                <div style={s.headerStat}><div style={{ ...s.headerStatNum, color: '#d4ac0d' }}>{pending.length}</div><div style={s.headerStatLabel}>Pending</div></div>
                {returned.length > 0 && <div style={s.headerStat}><div style={{ ...s.headerStatNum, color: '#e74c3c' }}>{returned.length}</div><div style={s.headerStatLabel}>Returned</div></div>}
                <div style={s.headerStat}><div style={{ ...s.headerStatNum, color: '#27ae60' }}>{active.length}</div><div style={s.headerStatLabel}>Active</div></div>
                <div style={s.headerStat}><div style={{ ...s.headerStatNum, color: '#666' }}>{completed.length}</div><div style={s.headerStatLabel}>Completed</div></div>
                {escalated.length > 0 && <div style={s.headerStat}><div style={{ ...s.headerStatNum, color: '#e67e22' }}>{escalated.length}</div><div style={s.headerStatLabel}>Escalated</div></div>}
              </div>
            </div>
          )}
        </div>
        {/* Mobile-only: buttons row + stats row */}
        {isMobile && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <button onClick={exportCSV} style={{ flex: 1, background: 'transparent', color: '#4a8f82', border: '1px solid #4a8f82', borderRadius: '6px', padding: '10px 12px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', cursor: 'pointer' }}>Export CSV</button>
              <button onClick={function() { setBulkMode(!bulkMode); setSelectedCases(new Set()); }} style={{ flex: 1, background: bulkMode ? '#1a1a2e' : 'transparent', color: bulkMode ? '#fff' : '#666', border: '1px solid #ccc', borderRadius: '6px', padding: '10px 12px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', cursor: 'pointer' }}>{bulkMode ? 'Exit Bulk' : 'Bulk Select'}</button>
              <button onClick={function() { setNewRequestModal(true); }} style={{ flex: 1, background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 12px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', cursor: 'pointer' }}>+ New</button>
            </div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div style={s.headerStat}><div style={{ ...s.headerStatNum, color: '#d4ac0d', fontSize: '22px' }}>{pending.length}</div><div style={s.headerStatLabel}>Pending</div></div>
              {returned.length > 0 && <div style={s.headerStat}><div style={{ ...s.headerStatNum, color: '#e74c3c', fontSize: '22px' }}>{returned.length}</div><div style={s.headerStatLabel}>Returned</div></div>}
              <div style={s.headerStat}><div style={{ ...s.headerStatNum, color: '#27ae60', fontSize: '22px' }}>{active.length}</div><div style={s.headerStatLabel}>Active</div></div>
              <div style={s.headerStat}><div style={{ ...s.headerStatNum, color: '#666', fontSize: '22px' }}>{completed.length}</div><div style={s.headerStatLabel}>Completed</div></div>
              {escalated.length > 0 && <div style={s.headerStat}><div style={{ ...s.headerStatNum, color: '#e67e22', fontSize: '22px' }}>{escalated.length}</div><div style={s.headerStatLabel}>Escalated</div></div>}
            </div>
          </div>
        )}
 
        {/* New Request Modal */}
        {newRequestModal && (
          <div style={s.modal}>
            <div style={{ ...s.modalCard, maxWidth: '580px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={s.modalTitle}>New Healing Request</div>

              {/* Patient Mode Toggle */}
              <div style={s.field}>
                <label style={s.label}>Patient</label>
                <div style={{ display: 'flex', gap: '0', border: '1px solid #e0e0e0', borderRadius: '5px', overflow: 'hidden', marginBottom: '10px' }}>
                  <button type="button"
                    onClick={function() { setNewReqForm(function(prev) { return Object.assign({}, prev, { patientMode: 'existing', selectedUserId: '', selectedUserName: '', userSearch: '', userSearchResults: [] }); }); }}
                    style={{ flex: 1, padding: '10px', border: 'none', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', background: newReqForm.patientMode === 'existing' ? '#4a8f82' : '#f9faf7', color: newReqForm.patientMode === 'existing' ? '#fff' : '#666' }}>
                    Existing User
                  </button>
                  <button type="button"
                    onClick={function() { setNewReqForm(function(prev) { return Object.assign({}, prev, { patientMode: 'manual', selectedUserId: '', selectedUserName: '', userSearch: '', userSearchResults: [] }); }); }}
                    style={{ flex: 1, padding: '10px', border: 'none', borderLeft: '1px solid #e0e0e0', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', background: newReqForm.patientMode === 'manual' ? '#4a8f82' : '#f9faf7', color: newReqForm.patientMode === 'manual' ? '#fff' : '#666' }}>
                    Manual Entry
                  </button>
                </div>

                {newReqForm.patientMode === 'existing' ? (
                  <div>
                    <input style={s.input} type="text" placeholder="Search by name, email or phone..."
                      value={newReqForm.userSearch}
                      onChange={function(e) { handleUserSearch(e.target.value); }} />
                    {userSearchLoading && <div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>Searching...</div>}
                    {newReqForm.selectedUserId ? (
                      <div style={{ marginTop: '8px', background: '#eafaf1', borderRadius: '5px', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a5c38' }}>{newReqForm.selectedUserName}</span>
                        <button type="button" onClick={function() { setNewReqForm(function(prev) { return Object.assign({}, prev, { selectedUserId: '', selectedUserName: '', userSearch: '' }); }); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: '16px' }}>×</button>
                      </div>
                    ) : newReqForm.userSearchResults.length > 0 && (
                      <div style={{ border: '1px solid #e0e0e0', borderRadius: '5px', marginTop: '4px', maxHeight: '180px', overflowY: 'auto' }}>
                        {newReqForm.userSearchResults.map(function(u) {
                          return (
                            <div key={u._id}
                              onClick={function() { setNewReqForm(function(prev) { return Object.assign({}, prev, { selectedUserId: u._id, selectedUserName: u.firstName + ' ' + u.lastName + ' (' + u.role + ')', userSearch: '', userSearchResults: [] }); }); }}
                              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5', fontSize: '13px', color: '#333' }}
                              onMouseEnter={function(e) { e.currentTarget.style.background = '#f9faf7'; }}
                              onMouseLeave={function(e) { e.currentTarget.style.background = '#fff'; }}>
                              <span style={{ fontWeight: '600' }}>{u.firstName} {u.lastName}</span>
                              <span style={{ color: '#999', marginLeft: '8px' }}>{u.email}</span>
                              <span style={{ marginLeft: '8px', fontSize: '11px', background: '#eaf4fb', color: '#2980b9', padding: '2px 8px', borderRadius: '10px' }}>{u.role}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <input style={s.input} type="text" placeholder="Enter patient full name..."
                    value={newReqForm.manualPatientName}
                    onChange={function(e) { setNewReqForm(function(prev) { return Object.assign({}, prev, { manualPatientName: e.target.value }); }); }} />
                )}
              </div>

              {/* For Self / Someone Else */}
              <div style={s.field}>
                <label style={s.label}>This request is for</label>
                <div style={{ display: 'flex', gap: '0', border: '1px solid #e0e0e0', borderRadius: '5px', overflow: 'hidden' }}>
                  <button type="button"
                    onClick={function() { setNewReqForm(function(prev) { return Object.assign({}, prev, { isForSelf: true, beneficiaryName: '', beneficiaryAge: '', beneficiaryRelationship: 'spouse', beneficiaryRelationshipOther: '' }); }); }}
                    style={{ flex: 1, padding: '10px', border: 'none', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', background: newReqForm.isForSelf ? '#4a8f82' : '#f9faf7', color: newReqForm.isForSelf ? '#fff' : '#666' }}>
                    Themselves
                  </button>
                  <button type="button"
                    onClick={function() { setNewReqForm(function(prev) { return Object.assign({}, prev, { isForSelf: false }); }); }}
                    style={{ flex: 1, padding: '10px', border: 'none', borderLeft: '1px solid #e0e0e0', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', background: !newReqForm.isForSelf ? '#4a8f82' : '#f9faf7', color: !newReqForm.isForSelf ? '#fff' : '#666' }}>
                    Someone Else
                  </button>
                </div>
              </div>

              {/* Beneficiary Details */}
              {!newReqForm.isForSelf && (
                <div style={{ background: '#fef9e7', border: '1px solid #d4ac0d', borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
                  <div style={{ ...s.label, marginBottom: '12px' }}>Beneficiary Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label style={s.label}>Full Name *</label>
                      <input style={s.input} type="text" placeholder="Beneficiary name"
                        value={newReqForm.beneficiaryName}
                        onChange={function(e) { setNewReqForm(function(prev) { return Object.assign({}, prev, { beneficiaryName: e.target.value }); }); }} />
                    </div>
                    <div>
                      <label style={s.label}>Age</label>
                      <input style={s.input} type="number" min="1" max="120" placeholder="Age"
                        value={newReqForm.beneficiaryAge}
                        onChange={function(e) { setNewReqForm(function(prev) { return Object.assign({}, prev, { beneficiaryAge: e.target.value }); }); }} />
                    </div>
                  </div>
                  <div>
                    <label style={s.label}>Relationship</label>
                    <select style={s.input} value={newReqForm.beneficiaryRelationship}
                      onChange={function(e) { setNewReqForm(function(prev) { return Object.assign({}, prev, { beneficiaryRelationship: e.target.value }); }); }}>
                      <option value="spouse">Spouse</option>
                      <option value="child">Child</option>
                      <option value="parent">Parent</option>
                      <option value="sibling">Sibling</option>
                      <option value="friend">Friend</option>
                      <option value="other">Other</option>
                    </select>
                    {newReqForm.beneficiaryRelationship === 'other' && (
                      <input style={{ ...s.input, marginTop: '8px' }} type="text" placeholder="Specify relationship..."
                        value={newReqForm.beneficiaryRelationshipOther}
                        onChange={function(e) { setNewReqForm(function(prev) { return Object.assign({}, prev, { beneficiaryRelationshipOther: e.target.value }); }); }} />
                    )}
                  </div>
                </div>
              )}

              {/* Condition & Description */}
              <div style={s.field}>
                <label style={s.label}>Condition / Health Concern *</label>
                <input style={s.input} type="text" placeholder="e.g. Back pain, Anxiety, Diabetes..."
                  value={newReqForm.condition}
                  onChange={function(e) { setNewReqForm(function(prev) { return Object.assign({}, prev, { condition: e.target.value }); }); }} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Description *</label>
                <textarea style={{ ...s.input, height: '90px', resize: 'vertical' }}
                  placeholder="Describe the condition in detail..."
                  value={newReqForm.description}
                  onChange={function(e) { setNewReqForm(function(prev) { return Object.assign({}, prev, { description: e.target.value }); }); }} />
              </div>

              {/* Urgency */}
              <div style={s.field}>
                <label style={s.label}>Urgency</label>
                <div style={{ display: 'flex', gap: '0', border: '1px solid #e0e0e0', borderRadius: '5px', overflow: 'hidden' }}>
                  {['normal', 'urgent', 'critical'].map(function(lvl) {
                    var colors = { normal: '#27ae60', urgent: '#d4ac0d', critical: '#e74c3c' };
                    return (
                      <button key={lvl} type="button"
                        onClick={function() { setNewReqForm(function(prev) { return Object.assign({}, prev, { urgency: lvl }); }); }}
                        style={{ flex: 1, padding: '10px', border: 'none', borderLeft: lvl !== 'normal' ? '1px solid #e0e0e0' : 'none', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', textTransform: 'capitalize', background: newReqForm.urgency === lvl ? colors[lvl] : '#f9faf7', color: newReqForm.urgency === lvl ? '#fff' : '#666' }}>
                        {lvl}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Medical Reports */}
              <div style={s.field}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={newReqForm.medicalReportsShared}
                    onChange={function(e) { setNewReqForm(function(prev) { return Object.assign({}, prev, { medicalReportsShared: e.target.checked }); }); }} />
                  <span style={{ fontSize: '13px', color: '#333' }}>Medical reports shared via WhatsApp</span>
                </label>
                {newReqForm.medicalReportsShared && (
                  <input style={{ ...s.input, marginTop: '8px' }} type="text" placeholder="Note about reports (optional)..."
                    value={newReqForm.medicalReportsNote}
                    onChange={function(e) { setNewReqForm(function(prev) { return Object.assign({}, prev, { medicalReportsNote: e.target.value }); }); }} />
                )}
              </div>

              <div style={s.modalBtns}>
                <button onClick={handleCreateRequest} disabled={submittingNewReq}
                  style={{ ...s.submitBtn, opacity: submittingNewReq ? 0.6 : 1 }}>
                  {submittingNewReq ? 'Creating...' : 'Create Request'}
                </button>
                <button onClick={function() { setNewRequestModal(false); setNewReqForm(emptyNewReq); }} style={s.cancelBtn}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Healer Modal */}
        {assignModal && (
          <div style={s.modal}>
            <div style={s.modalCard}>
              <div style={s.modalTitle}>{isReassignment ? 'Reassign Healer' : 'Assign Healer'}</div>
              <div style={s.modalInfo}>
                <div style={s.modalInfoName}>{(assignModal.patient && (assignModal.patient.firstName || assignModal.patient.lastName)) ? (assignModal.patient.firstName + ' ' + assignModal.patient.lastName).trim() : (assignModal.manualPatientName || 'Unknown')}</div>
                <div style={s.modalInfoSub}>{assignModal.condition} | {assignModal.urgency}</div>
                {isReassignment && assignModal.assignedHealer && (
                  <div style={{ fontSize: '12px', color: '#e74c3c', marginTop: '4px' }}>
                    Currently assigned to: {assignModal.assignedHealer.firstName} {assignModal.assignedHealer.lastName} · {assignModal.totalSessionsCompleted || 0} sessions done
                  </div>
                )}
                {!assignModal.isForSelf && assignModal.beneficiaryName && (
                  <div style={{ fontSize: '12px', color: '#c9a84c', marginTop: '4px' }}>Requesting for: {assignModal.beneficiaryName} ({assignModal.beneficiaryRelationship})</div>
                )}
              </div>
              <div style={s.field}>
                <label style={s.label}>Select Healer</label>
                <select style={s.input} value={selectedHealer} onChange={function(e) { setSelectedHealer(e.target.value); }}>
                  <option value="">Choose a healer...</option>
                  {healers.map(function(h) {
                    var activeCases = requests.filter(function(r) { return r.assignedHealer && r.assignedHealer._id === h._id && (r.status === 'assigned' || r.status === 'in_progress'); }).length;
                    var atCapacity = h.maxActivePatients && activeCases >= h.maxActivePatients;
                    return <option key={h._id} value={h._id}>{h.firstName} {h.lastName} — {activeCases} active{h.maxActivePatients ? '/' + h.maxActivePatients : ''}{atCapacity ? ' ⚠ Full' : ''}{h.isAvailable ? '' : ' (Unavailable)'}</option>;
                  })}
                </select>
              </div>
              {/* Capacity warning */}
              {selectedHealer && (function() {
                var h = healers.find(function(x) { return x._id === selectedHealer; });
                if (!h || !h.maxActivePatients) return null;
                var activeCases = requests.filter(function(r) { return r.assignedHealer && r.assignedHealer._id === h._id && (r.status === 'assigned' || r.status === 'in_progress'); }).length;
                if (activeCases < h.maxActivePatients) return null;
                return (
                  <div style={{ background: '#fef9e7', border: '1px solid #d4ac0d', borderRadius: '6px', padding: '10px 14px', marginTop: '-8px', fontSize: '12px', color: '#7d6608' }}>
                    ⚠ {h.firstName} {h.lastName} is at capacity ({activeCases}/{h.maxActivePatients} active patients). Assigning will exceed their limit.
                  </div>
                );
              })()}

              {/* Reassignment-specific fields */}
              {isReassignment && (
                <div>
                  <div style={s.field}>
                    <label style={s.label}>Reassignment Reason *</label>
                    <textarea style={{ ...s.input, height: '70px', resize: 'vertical' }}
                      value={transferReason} onChange={function(e) { setTransferReason(e.target.value); }}
                      placeholder="Why is this patient being reassigned? (internal only)" required />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Session Count</label>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '6px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#333', cursor: 'pointer' }}>
                        <input type="radio" name="sessionCountOption" value="carry_over" checked={sessionCountOption === 'carry_over'}
                          onChange={function() { setSessionCountOption('carry_over'); }} />
                        Carry Over ({assignModal.sessions_remaining || 0} remaining)
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#333', cursor: 'pointer' }}>
                        <input type="radio" name="sessionCountOption" value="set_new"
                          checked={sessionCountOption === 'set_new'}
                          onChange={function() { setSessionCountOption('set_new'); }} />
                        Set New Count
                      </label>
                    </div>
                    {sessionCountOption === 'set_new' && (
                      <input style={{ ...s.input, marginTop: '8px', maxWidth: '200px' }} type="number" min="1"
                        value={newSessionsCount} onChange={function(e) { setNewSessionsCount(e.target.value); }}
                        placeholder="New sessions count" />
                    )}
                  </div>
                </div>
              )}

              <div style={s.field}>
                <label style={s.label}>Total Sessions Planned *</label>
                <input style={{ ...s.input, maxWidth: '200px' }} type="number" min="1"
                  value={sessionsPlanned} onChange={function(e) { setSessionsPlanned(e.target.value); }}
                  placeholder="e.g. 10" required />
              </div>
              <div style={s.field}>
                <label style={s.label}>Internal Remarks (not visible to patient)</label>
                <textarea style={{ ...s.input, height: '70px', resize: 'vertical', background: '#fef9e7', borderLeft: '4px solid #d4ac0d' }}
                  value={sessionRemarks} onChange={function(e) { setSessionRemarks(e.target.value); }}
                  placeholder="Internal notes about this assignment..." />
              </div>
              <div style={s.field}>
                <label style={s.label}>Admin Notes (optional)</label>
                <textarea style={{ ...s.input, height: '60px', resize: 'vertical' }}
                  value={adminNotes} onChange={function(e) { setAdminNotes(e.target.value); }}
                  placeholder="Any notes for the healer..." />
              </div>
              <div style={s.field}>
                <label style={s.label}>Show Healer Name to Patient</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                  <button type="button"
                    onClick={function() { setShowHealerName(!showHealerName); }}
                    style={{
                      width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', position: 'relative',
                      background: showHealerName ? '#4a8f82' : '#ccc', transition: 'background 0.2s'
                    }}>
                    <div style={{
                      width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: '2px',
                      left: showHealerName ? '22px' : '2px', transition: 'left 0.2s'
                    }}></div>
                  </button>
                  <span style={{ fontSize: '13px', color: showHealerName ? '#4a8f82' : '#999', fontWeight: '600' }}>
                    {showHealerName ? 'ON — Patient can see healer name' : 'OFF — Shows as "Healer"'}
                  </span>
                </div>
              </div>
              <div style={s.modalBtns}>
                <button onClick={handleAssign} style={s.submitBtn}>{isReassignment ? 'Reassign Healer' : 'Assign Healer'}</button>
                <button onClick={resetAssignModal} style={s.cancelBtn}>Cancel</button>
              </div>
            </div>
          </div>
        )}
 
 
        {/* Return to Same Healer Modal */}
        {returnToHealerModal && (
          <div style={s.modal}>
            <div style={s.modalCard}>
              <div style={s.modalTitle}>Return to Same Healer</div>
              <div style={s.modalInfo}>
                <div style={s.modalInfoName}>{(returnToHealerModal.patient && (returnToHealerModal.patient.firstName || returnToHealerModal.patient.lastName)) ? (returnToHealerModal.patient.firstName + ' ' + returnToHealerModal.patient.lastName).trim() : (returnToHealerModal.manualPatientName || 'Unknown')}</div>
                <div style={s.modalInfoSub}>{returnToHealerModal.condition} | Healer: {returnToHealerModal.assignedHealer ? returnToHealerModal.assignedHealer.firstName + ' ' + returnToHealerModal.assignedHealer.lastName : ''}</div>
              </div>
              <div style={s.field}>
                <label style={s.label}>Note to Healer (optional)</label>
                <textarea style={{ ...s.input, height: '70px', resize: 'vertical' }}
                  value={returnToHealerNote} onChange={function(e) { setReturnToHealerNote(e.target.value); }}
                  placeholder="Any instructions for the healer..." />
              </div>
              <div style={s.field}>
                <label style={s.label}>Show Healer Name to Patient</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                  <button type="button"
                    onClick={function() { setReturnToHealerShowName(!returnToHealerShowName); }}
                    style={{
                      width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', position: 'relative',
                      background: returnToHealerShowName ? '#4a8f82' : '#ccc', transition: 'background 0.2s'
                    }}>
                    <div style={{
                      width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: '2px',
                      left: returnToHealerShowName ? '22px' : '2px', transition: 'left 0.2s'
                    }}></div>
                  </button>
                  <span style={{ fontSize: '13px', color: returnToHealerShowName ? '#4a8f82' : '#999', fontWeight: '600' }}>
                    {returnToHealerShowName ? 'ON — Patient can see healer name' : 'OFF — Shows as "Healer"'}
                  </span>
                </div>
              </div>
              <div style={s.modalBtns}>
                <button onClick={handleReturnToSameHealer} style={s.submitBtn}>Confirm Return</button>
                <button onClick={function() { setReturnToHealerModal(null); setReturnToHealerNote(''); setReturnToHealerShowName(false); }} style={s.cancelBtn}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Close Early Modal */}
        {earlyCompleteModal && (
          <div style={s.modal}>
            <div style={{ ...s.modalCard, maxWidth: '500px' }}>
              <div style={s.modalTitle}>Close Case Early</div>
              <div style={s.modalInfo}>
                <div style={s.modalInfoName}>
                  {(earlyCompleteModal.patient && (earlyCompleteModal.patient.firstName || earlyCompleteModal.patient.lastName))
                    ? (earlyCompleteModal.patient.firstName + ' ' + earlyCompleteModal.patient.lastName).trim()
                    : (earlyCompleteModal.manualPatientName || 'Unknown')}
                </div>
                <div style={s.modalInfoSub}>
                  {earlyCompleteModal.condition} &nbsp;|&nbsp; Sessions done: {earlyCompleteModal.totalSessionsCompleted} of {earlyCompleteModal.sessions_planned || '?'} planned
                </div>
              </div>
              <div style={{ background: '#eafaf1', border: '1px solid #a9dfbf', borderRadius: '6px', padding: '12px 14px', marginBottom: '16px', fontSize: '12px', color: '#1a6b5a', lineHeight: '1.6' }}>
                Closing early will set sessions planned to <strong>{earlyCompleteModal.totalSessionsCompleted}</strong> (matching actual sessions done). All active patient activities will be ended and a completion email will be sent to the patient.
              </div>
              <div style={s.field}>
                <label style={s.label}>Reason for Early Closure *</label>
                <textarea style={{ ...s.input, height: '80px', resize: 'vertical' }}
                  placeholder="e.g. Patient recovered ahead of schedule..."
                  value={earlyCompleteReason}
                  onChange={function(e) { setEarlyCompleteReason(e.target.value); }} />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button onClick={function() { setEarlyCompleteModal(null); setEarlyCompleteReason(''); }}
                  style={s.cancelBtn} disabled={submittingEarlyComplete}>Cancel</button>
                <button onClick={handleEarlyComplete} disabled={submittingEarlyComplete}
                  style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: '4px', padding: '12px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: submittingEarlyComplete ? 'not-allowed' : 'pointer', opacity: submittingEarlyComplete ? 0.7 : 1 }}>
                  {submittingEarlyComplete ? 'Closing...' : 'Confirm — Close Early'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Drop Patient Modal with Email Preview */}
        {dropModal && (
          <div style={s.modal}>
            <div style={{ ...s.modalCard, maxWidth: isMobile ? '100%' : '620px' }}>
              <div style={s.modalTitle}>Drop Patient</div>
              <div style={s.modalInfo}>
                <div style={s.modalInfoName}>{(dropModal.patient && (dropModal.patient.firstName || dropModal.patient.lastName)) ? (dropModal.patient.firstName + ' ' + dropModal.patient.lastName).trim() : (dropModal.manualPatientName || 'Unknown')}</div>
                <div style={s.modalInfoSub}>{dropModal.condition} | Sessions done: {dropModal.totalSessionsCompleted || 0}</div>
                <div style={{ fontSize: '12px', color: '#e74c3c', marginTop: '4px' }}>This action will end the healing program and send a notification email to the patient.</div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '10px' }}>Email Preview</div>
                <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden', maxHeight: '300px', overflowY: 'auto' }}>
                  <div style={{ background: 'linear-gradient(135deg, #071e12, #0c2617)', padding: '16px', textAlign: 'center' }}>
                    <img src="https://pranicwellnesscentre.com/wp-content/uploads/2018/10/PH-Revised-Logo.png" alt="PWC" style={{ height: '32px' }} />
                  </div>
                  <div style={{ padding: '24px', fontSize: '13px', color: '#333', lineHeight: '1.7' }}>
                    <p><strong>Subject:</strong> Your Healing Journey at Pranic Wellness Centre</p>
                    <p>Dear {(dropModal.patient && dropModal.patient.firstName) ? dropModal.patient.firstName : (dropModal.manualPatientName ? dropModal.manualPatientName.split(' ')[0] : 'Patient')},</p>
                    <p>Thank you for being a part of the Pranic Wellness Centre family. We are grateful for the trust you have placed in us during your healing journey.</p>
                    <p>At this time, your current healing program has been concluded. If you have any questions or would like to discuss the next steps in your wellness journey, we warmly invite you to reach out to us directly.</p>
                    <p>Please call us at <strong>+91 91768 80807</strong> or visit us in person at our centre in Perungudi, Chennai.</p>
                    <p style={{ fontStyle: 'italic', color: '#666' }}>With healing blessings,<br />Pranic Wellness Centre, Chennai</p>
                  </div>
                  <div style={{ background: '#1a1a2e', padding: '14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Pranic Wellness Centre | Kurinji Nagar, Perungudi, Chennai 600096</div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)' }}>This is an auto-generated email. Please do not reply to this email. For any questions, reach out to us at +91 91768 80807</div>
                  </div>
                </div>
              </div>
              <div style={s.modalBtns}>
                <button onClick={handleDropPatient} disabled={dropping}
                  style={{ ...s.submitBtn, background: '#e74c3c', opacity: dropping ? 0.6 : 1 }}>
                  {dropping ? 'Processing...' : 'Confirm Drop & Send Email'}
                </button>
                <button onClick={function() { setDropModal(null); }} style={s.cancelBtn}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Assign Modal (#4) */}
        {bulkAssignModal && (
          <div style={s.modal}>
            <div style={s.modalCard}>
              <div style={s.modalTitle}>Bulk Assign — {selectedCases.size} case(s)</div>
              <div style={{ background: '#f9faf7', borderRadius: '6px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: '#555' }}>
                Assigning {selectedCases.size} pending case(s) to the same healer with the same session count.
              </div>
              <div style={s.field}>
                <label style={s.label}>Select Healer *</label>
                <select style={s.input} value={bulkHealer} onChange={function(e) { setBulkHealer(e.target.value); }}>
                  <option value="">— Choose healer —</option>
                  {healers.map(function(h) {
                    return <option key={h._id} value={h._id}>{h.firstName} {h.lastName}</option>;
                  })}
                </select>
              </div>
              <div style={s.field}>
                <label style={s.label}>Sessions Planned per Case *</label>
                <input style={{ ...s.input, maxWidth: '200px' }} type="number" min="1"
                  value={bulkSessions} onChange={function(e) { setBulkSessions(e.target.value); }}
                  placeholder="e.g. 10" />
              </div>
              <div style={s.modalBtns}>
                <button onClick={handleBulkAssign} disabled={bulkAssigning}
                  style={{ ...s.submitBtn, opacity: bulkAssigning ? 0.6 : 1 }}>
                  {bulkAssigning ? 'Assigning...' : 'Assign All'}
                </button>
                <button onClick={function() { setBulkAssignModal(false); }} style={s.cancelBtn}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Send Report Modal */}
        {reportModal && (
          <div style={s.modal} onClick={function(e) { if (e.target === e.currentTarget) setReportModal(null); }}>
            <div style={{ ...s.modalCard, maxWidth: '520px' }}>
              <div style={s.modalTitle}>Send Healing Report</div>
              <div style={{ background: '#f0f9f7', border: '1px solid #b2dfdb', borderRadius: '6px', padding: '12px 14px', marginBottom: '20px' }}>
                <div style={{ fontWeight: '700', fontSize: '13px', color: '#1a6b5a' }}>
                  {reportModal.patient ? reportModal.patient.firstName + ' ' + reportModal.patient.lastName : reportModal.manualPatientName || 'Patient'}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{reportModal.condition} | {reportModal.totalSessionsCompleted || 0} sessions | {reportModal.status}</div>
              </div>

              {/* Recipient search */}
              <div style={s.field}>
                <label style={s.label}>Recipient — Search registered user</label>
                <input style={s.input} type="text" placeholder="Type name or email..."
                  value={reportForm.recipientSearch}
                  onChange={async function(e) {
                    var q = e.target.value;
                    setReportForm(function(p) { return Object.assign({}, p, { recipientSearch: q, recipientEmail: '', recipientName: '' }); });
                    setReportUserResults([]);
                    if (q.length < 2) return;
                    try {
                      var res = await getUsers({ search: q, limit: 8 });
                      setReportUserResults(res.data.users || []);
                    } catch(err) {}
                  }} />
                {reportUserResults.length > 0 && (
                  <div style={{ border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px', background: '#fff', maxHeight: '180px', overflowY: 'auto' }}>
                    {reportUserResults.map(function(u) {
                      return (
                        <div key={u._id}
                          onClick={function() {
                            setReportForm(function(p) { return Object.assign({}, p, { recipientSearch: u.firstName + ' ' + u.lastName + ' (' + u.email + ')', recipientEmail: u.email, recipientName: u.firstName + ' ' + u.lastName }); });
                            setReportUserResults([]);
                          }}
                          style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: '13px' }}>
                          <span style={{ fontWeight: '600' }}>{u.firstName} {u.lastName}</span>
                          <span style={{ color: '#999', marginLeft: '8px', fontSize: '12px' }}>{u.email}</span>
                          <span style={{ marginLeft: '8px', fontSize: '11px', background: '#f0f0f0', borderRadius: '4px', padding: '1px 6px', color: '#666' }}>{u.role}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Manual email fallback */}
              {!reportForm.recipientEmail && reportForm.recipientSearch.length > 0 && reportUserResults.length === 0 && (
                <div style={s.field}>
                  <label style={s.label}>Or enter email manually</label>
                  <input style={s.input} type="email" placeholder="recipient@email.com"
                    value={reportForm.recipientEmail}
                    onChange={function(e) { setReportForm(function(p) { return Object.assign({}, p, { recipientEmail: e.target.value, recipientName: e.target.value }); }); }} />
                </div>
              )}

              {/* Include notes toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <input type="checkbox" id="includeNotes" checked={reportForm.includeNotes}
                  onChange={function(e) { setReportForm(function(p) { return Object.assign({}, p, { includeNotes: e.target.checked }); }); }}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                <label htmlFor="includeNotes" style={{ fontSize: '13px', color: '#333', cursor: 'pointer' }}>Include internal case notes in the report</label>
              </div>

              {reportForm.recipientEmail && (
                <div style={{ background: '#eafaf1', border: '1px solid #a9dfbf', borderRadius: '4px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#27ae60' }}>
                  PDF will be emailed to: <strong>{reportForm.recipientEmail}</strong>
                </div>
              )}

              <div style={s.modalBtns}>
                <button
                  disabled={sendingReport || !reportForm.recipientEmail}
                  onClick={async function() {
                    setSendingReport(true);
                    try {
                      await sendHealingReport(reportModal._id, {
                        recipientEmail: reportForm.recipientEmail,
                        recipientName: reportForm.recipientName,
                        includeNotes: reportForm.includeNotes
                      });
                      alert('Report sent to ' + reportForm.recipientEmail);
                      setReportModal(null);
                    } catch(e) {
                      alert(e.response ? e.response.data.message : 'Failed to send report');
                    } finally {
                      setSendingReport(false);
                    }
                  }}
                  style={{ ...s.saveBtn, opacity: (!reportForm.recipientEmail || sendingReport) ? 0.6 : 1 }}>
                  {sendingReport ? 'Generating & Sending...' : 'Send Report'}
                </button>
                <button onClick={function() { setReportModal(null); }} style={s.cancelBtn}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Selection Toolbar (#4) */}
        {bulkMode && selectedCases.size > 0 && (
          <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: '#1a1a2e', color: '#fff', borderRadius: '10px', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: '16px', zIndex: 999, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: '13px', fontWeight: '700' }}>{selectedCases.size} selected</span>
            <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.2)' }}></div>
            {activeTab === 'pending' && (
              <button onClick={function() { setBulkAssignModal(true); }}
                style={{ background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                Assign to Healer
              </button>
            )}
            <button onClick={function() { setSelectedCases(new Set()); }}
              style={{ background: 'transparent', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '6px', padding: '8px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
              Clear
            </button>
            <button onClick={function() {
              var ids = displayList.map(function(r) { return r._id; });
              setSelectedCases(new Set(ids));
            }} style={{ background: 'transparent', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '6px', padding: '8px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
              Select All ({displayList.length})
            </button>
          </div>
        )}

        {/* Tabs */}
        <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '24px' }}>
          <div style={{ display: 'inline-flex', gap: '4px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', minWidth: 'min-content' }}>
            {[
              { key: 'pending', label: 'Pending (' + pending.length + ')' },
              { key: 'returned', label: 'Returned (' + returned.length + ')' },
              { key: 'active', label: 'Active (' + active.length + ')' },
              { key: 'completed', label: 'Completed (' + completed.length + ')' }
            ].map(function(tab) {
              return (
                <button key={tab.key} onClick={function() { setActiveTab(tab.key); }}
                  style={{ ...(activeTab === tab.key ? { ...s.tab, ...s.tabActive } : s.tab), whiteSpace: 'nowrap' }}>
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
 
        {loading ? (
          <div style={s.loading}>Loading healing requests...</div>
        ) : displayList.length === 0 ? (
          <div style={s.emptyCard}>
            <div style={s.emptyTitle}>No {activeTab} requests</div>
            <div style={s.emptySub}>
              {activeTab === 'pending' ? 'All requests have been assigned.' :
               activeTab === 'returned' ? 'No returned patients in queue.' :
               activeTab === 'active' ? 'No active healing sessions.' :
               'No completed cases yet.'}
            </div>
          </div>
        ) : (
          <div style={s.requestList}>
            {displayList.map(function(req) {
              return (
                <div key={req._id} style={{ ...s.requestCard, border: selectedCases.has(req._id) ? '2px solid #4a8f82' : '1px solid #e0e0e0' }}>

                  {/* Bulk checkbox + inactivity badge (#4, #18) */}
                  {(function() {
                    var days = getDaysSinceActivity(req);
                    var show = bulkMode || (days !== null && days >= 14);
                    if (!show) return null;
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        {bulkMode && (
                          <input type="checkbox" checked={selectedCases.has(req._id)}
                            onChange={function(e) {
                              setSelectedCases(function(prev) {
                                var next = new Set(prev);
                                e.target.checked ? next.add(req._id) : next.delete(req._id);
                                return next;
                              });
                            }}
                            style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#4a8f82' }} />
                        )}
                        {days !== null && days >= 14 && (
                          <span style={{ background: '#fef5ec', color: '#e67e22', border: '1px solid #e67e22', borderRadius: '12px', padding: '3px 10px', fontSize: '11px', fontWeight: '700' }}>
                            ⚠ {days} days inactive
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  {/* Request Header */}
                  <div style={s.requestHeader}>
                    <div style={s.requestHeaderLeft}>
                      <div style={s.patientAvatar}>
                        {req.patient && req.patient.firstName ? req.patient.firstName.charAt(0) : (req.manualPatientName ? req.manualPatientName.charAt(0) : '?')}
                        {req.patient && req.patient.lastName ? req.patient.lastName.charAt(0) : (req.manualPatientName ? (req.manualPatientName.split(' ')[1] || '').charAt(0) : '')}
                      </div>
                      <div>
                        {(function() {
                          var pName = (req.patient && (req.patient.firstName || req.patient.lastName))
                            ? (req.patient.firstName + ' ' + req.patient.lastName).trim()
                            : (req.manualPatientName || (req.patient && req.patient.email) || 'Unknown');
                          var pContact = req.patient
                            ? [req.patient.email, req.patient.phone].filter(Boolean).join(' | ')
                            : (req.manualPatientName ? 'Manual entry — no account' : '');
                          return (
                            <>
                              <div style={s.patientName}>
                                {pName}
                                {req.createdByAdmin && <span style={{ marginLeft: '8px', fontSize: '10px', background: '#eaf4fb', color: '#2980b9', padding: '2px 8px', borderRadius: '10px', fontWeight: '700' }}>Admin Created</span>}
                                {!req.isForSelf && req.beneficiaryName && (
                                  <span style={s.beneficiaryTag}>requesting for {req.beneficiaryName}</span>
                                )}
                              </div>
                              <div style={s.patientContact}>{pContact}</div>
                            </>
                          );
                        })()}
                        <div style={s.requestCondition}>{req.condition}</div>
                      </div>
                    </div>
                    <div style={s.requestHeaderRight}>
                      <div style={s.badgeRow}>
                        <span style={{ ...s.badge, background: urgencyColors[req.urgency] ? urgencyColors[req.urgency].bg : '#f0f0f0', color: urgencyColors[req.urgency] ? urgencyColors[req.urgency].color : '#666' }}>{req.urgency}</span>
                        <span style={{ ...s.badge, background: statusColors[req.status] ? statusColors[req.status].bg : '#f0f0f0', color: statusColors[req.status] ? statusColors[req.status].color : '#666' }}>{req.status ? req.status.replace('_', ' ') : ''}</span>
                        {req.followUpDate && (function() {
                          var isOverdue = new Date(req.followUpDate) < new Date(new Date().toDateString());
                          return <span style={{ ...s.badge, background: isOverdue ? '#fdf0f0' : '#fef9e7', color: isOverdue ? '#e74c3c' : '#d4ac0d' }}>
                            {isOverdue ? '⚠ Follow-up Overdue' : '📅 Follow-up ' + new Date(req.followUpDate).toLocaleDateString('en-IN')}
                          </span>;
                        })()}
                        {req.medicalReportsShared && <span style={{ ...s.badge, background: '#eafaf1', color: '#25D366' }}>Reports Shared</span>}
                        {req.consentComplementary && req.consentLiability && req.consentTestimonial && (
                          <span style={{ ...s.badge, background: '#eafaf1', color: '#27ae60' }}>Consent Given</span>
                        )}
                      </div>
                      <div style={s.requestDate}>{new Date(req.requestedAt).toLocaleDateString('en-IN')}</div>
                    </div>
                  </div>
 
                  {/* Description */}
                  <div style={s.requestDesc}>{req.description}</div>
 
                  {/* Info Row */}
                  <div style={s.infoRow}>
                    <div style={s.infoBox}>
                      <div style={s.infoLabel}>Assigned Healer</div>
                      <div style={s.infoValue}>{req.assignedHealer ? req.assignedHealer.firstName + ' ' + req.assignedHealer.lastName : <span style={{ color: '#e74c3c' }}>Unassigned</span>}</div>
                    </div>
                    <div style={s.infoBox}>
                      <div style={s.infoLabel}>Sessions Done</div>
                      <div style={s.infoValue}>{req.totalSessionsCompleted}</div>
                    </div>
                    <div style={s.infoBox}>
                      <div style={s.infoLabel}>Requested On</div>
                      <div style={s.infoValue}>{new Date(req.requestedAt).toLocaleDateString('en-IN')}</div>
                    </div>
                    {!req.isForSelf && req.beneficiaryName && (
                      <div style={s.infoBox}>
                        <div style={s.infoLabel}>Patient</div>
                        <div style={s.infoValue}>{req.beneficiaryName}, Age {req.beneficiaryAge}</div>
                      </div>
                    )}
                  </div>

                  {/* Session Planning Summary (v2.0) */}
                  {req.sessions_planned && (
                    <div style={s.sessionSummaryRow}>
                      <div style={s.sessionSummaryBox}>
                        <div style={s.sessionSummaryNum}>{req.sessions_planned}</div>
                        <div style={s.sessionSummaryLabel}>Planned</div>
                      </div>
                      <div style={s.sessionSummaryBox}>
                        <div style={s.sessionSummaryNum}>{req.totalSessionsCompleted || 0}</div>
                        <div style={s.sessionSummaryLabel}>Done</div>
                      </div>
                      <div style={s.sessionSummaryBox}>
                        <div style={{ ...s.sessionSummaryNum, color: (req.sessions_remaining || 0) > 0 ? '#27ae60' : '#e74c3c' }}>
                          {req.sessions_remaining != null ? req.sessions_remaining : (req.sessions_planned - (req.totalSessionsCompleted || 0))}
                        </div>
                        <div style={s.sessionSummaryLabel}>Remaining</div>
                      </div>
                      <div style={s.sessionSummaryBox}>
                        <span style={{
                          ...s.badge,
                          background: req.assignment_status === 'completed' ? '#f0f0f0' : req.assignment_status === 'returned' ? '#fdf0f0' : req.assignment_status === 'transferred' ? '#fef9e7' : '#eafaf1',
                          color: req.assignment_status === 'completed' ? '#666' : req.assignment_status === 'returned' ? '#e74c3c' : req.assignment_status === 'transferred' ? '#d4ac0d' : '#27ae60'
                        }}>
                          {req.assignment_status || 'active'}
                        </span>
                      </div>
                    </div>
                  )}
 
                  {/* Returned Patient Info */}
                  {req.assignment_status === 'returned' && (
                    <div style={s.returnedInfoBox}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <span style={s.returnedBadge}>Returned to Admin</span>
                        {req.return_reason_code && (
                          <span style={{
                            ...s.badge,
                            background: reasonCodeColors[req.return_reason_code] ? reasonCodeColors[req.return_reason_code].bg : '#f0f0f0',
                            color: reasonCodeColors[req.return_reason_code] ? reasonCodeColors[req.return_reason_code].color : '#666'
                          }}>{req.return_reason_code ? req.return_reason_code.replace('_', ' ') : ''}</span>
                        )}
                        {req.returned_on && (
                          <span style={{ fontSize: '12px', color: '#999' }}>on {new Date(req.returned_on).toLocaleDateString('en-IN')}</span>
                        )}
                      </div>
                      {req.healer_return_remarks && (
                        <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.6', background: '#fef9e7', borderLeft: '4px solid #d4ac0d', padding: '10px 14px', borderRadius: '4px' }}>
                          <div style={{ fontSize: '10px', fontWeight: '700', color: '#7D6608', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Healer Remarks (Internal)</div>
                          {req.healer_return_remarks}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div style={s.actionRow}>
                    {req.status === 'pending' && req.assignment_status !== 'returned' && (
                      <button onClick={function() { setAssignModal(req); }} style={s.assignBtn}>Assign Healer</button>
                    )}
                    {req.status === 'pending' && req.assignment_status !== 'returned' && (
                      <button onClick={function() { setDropModal(req); }} style={s.dropBtn}>Drop Patient</button>
                    )}
                    {req.assignment_status === 'returned' && (
                      <button onClick={function() { setAssignModal(req); }} style={s.assignBtn}>Reassign to Another Healer</button>
                    )}
                    {req.assignment_status === 'returned' && (
                      <button onClick={function() { setReturnToHealerModal(req); }} style={s.reassignBtn}>Return to Same Healer</button>
                    )}
                    {req.assignment_status === 'returned' && (
                      <button onClick={function() { setDropModal(req); }} style={s.dropBtn}>Drop Patient</button>
                    )}
                    {(req.status === 'assigned' || req.status === 'in_progress') && (
                      <button onClick={function() { setAssignModal(req); }} style={s.reassignBtn}>Reassign Healer</button>
                    )}
                    {(req.status === 'assigned' || req.status === 'in_progress') && (
                      <button onClick={function() { setEarlyCompleteModal(req); setEarlyCompleteReason(''); }}
                        style={{ ...s.assignBtn, background: '#27ae60' }}>
                        Close Early
                      </button>
                    )}
                    {req.status === 'completed' && req.assignment_status === 'completed' && (
                      <button onClick={function() { handleReopen(req._id); }} style={s.reopenBtn}>Reopen Case</button>
                    )}
                    <button onClick={function() {
                      setExpandedRequest(expandedRequest === req._id ? null : req._id);
                      if (expandedRequest !== req._id) { fetchTransferLog(req._id); }
                    }} style={s.expandBtn}>
                      {expandedRequest === req._id ? 'Hide History' : 'Session History (' + (req.sessions ? req.sessions.length : 0) + ')'}
                    </button>
                    {(transferLogs[req._id] || []).length > 0 && (
                      <button onClick={function() { setTransferPanelOpen(transferPanelOpen === req._id ? null : req._id); }}
                        style={{ ...s.expandBtn, color: '#d4ac0d', borderColor: '#d4ac0d' }}>
                        {transferPanelOpen === req._id ? 'Hide Transfers' : 'Transfers (' + transferLogs[req._id].length + ')'}
                      </button>
                    )}
                    <button onClick={function() {
                      setNotesOpen(notesOpen === req._id ? null : req._id);
                      if (!caseNotesMap[req._id] && req.caseNotes) {
                        setCaseNotesMap(function(prev) { return Object.assign({}, prev, { [req._id]: req.caseNotes }); });
                      }
                    }} style={{ ...s.expandBtn, color: '#2980b9', borderColor: '#2980b9' }}>
                      {notesOpen === req._id ? 'Hide Notes' : 'Notes (' + ((caseNotesMap[req._id] || req.caseNotes || []).length) + ')'}
                    </button>
                    <button onClick={function() {
                      setActivitiesOpen(activitiesOpen === req._id ? null : req._id);
                      if (!activitiesMap[req._id]) {
                        setActivitiesMap(function(prev) { return Object.assign({}, prev, { [req._id]: req.activities || [] }); });
                      }
                    }} style={{ ...s.expandBtn, color: '#8e44ad', borderColor: '#8e44ad' }}>
                      {activitiesOpen === req._id ? 'Hide Activities' : 'Activities (' + (req.activities ? req.activities.filter(function(a) { return a.isActive; }).length : 0) + ')'}
                    </button>
                    {req.patient && req.patient.phone && (
                      <a
                        href={'https://wa.me/' + req.patient.phone.replace(/[^0-9]/g, '') + '?text=' + encodeURIComponent('Hello ' + (req.patient.firstName || '') + ', this is Pranic Wellness Centre. Regarding your healing request for ' + (req.condition || 'your condition') + '.')}
                        target="_blank"
                        rel="noreferrer"
                        title={'WhatsApp ' + req.patient.firstName}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '8px 14px', border: '1px solid #25D366', borderRadius: '4px', color: '#25D366', fontSize: '12px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", textDecoration: 'none', letterSpacing: '0.5px' }}
                      >
                        <FaWhatsapp style={{ fontSize: '14px' }} /> WhatsApp
                      </a>
                    )}
                    <button onClick={function() {
                      if (followUpOpen === req._id) { setFollowUpOpen(null); return; }
                      setFollowUpOpen(req._id);
                      setFollowUpForm({ date: req.followUpDate ? new Date(req.followUpDate).toISOString().split('T')[0] : '', note: req.followUpNote || '' });
                    }} style={{ ...s.expandBtn, color: '#e67e22', borderColor: '#e67e22' }}>
                      {followUpOpen === req._id ? 'Hide Follow-up' : req.followUpDate ? 'Edit Follow-up' : 'Set Follow-up'}
                    </button>
                    {(req.status === 'completed' || req.status === 'dropped') && (
                      <button onClick={function() {
                        setReportModal(req);
                        setReportForm({ recipientSearch: '', recipientEmail: '', recipientName: '', includeNotes: false });
                        setReportUserResults([]);
                      }} style={{ ...s.expandBtn, color: '#1a6b5a', borderColor: '#1a6b5a', fontWeight: '700' }}>
                        Send Report
                      </button>
                    )}
                  </div>
 
                  {/* Follow-up Panel */}
                  {followUpOpen === req._id && (
                    <div style={{ marginTop: '12px', background: '#fffbf5', border: '1px solid #f0d9b5', borderRadius: '6px', padding: '16px' }}>
                      <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#e67e22', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Set Follow-up Reminder</div>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1, minWidth: '140px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Date</div>
                          <input type="date" value={followUpForm.date}
                            onChange={function(e) { setFollowUpForm(function(p) { return Object.assign({}, p, { date: e.target.value }); }); }}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ flex: 2, minWidth: '200px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Note (optional)</div>
                          <input type="text" value={followUpForm.note} placeholder="e.g. Check session progress"
                            onChange={function(e) { setFollowUpForm(function(p) { return Object.assign({}, p, { note: e.target.value }); }); }}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button disabled={savingFollowUp || !followUpForm.date}
                            onClick={function() { handleSaveFollowUp(req._id); }}
                            style={{ padding: '8px 18px', background: '#e67e22', color: '#fff', border: 'none', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer', opacity: (!followUpForm.date || savingFollowUp) ? 0.6 : 1 }}>
                            {savingFollowUp ? 'Saving...' : 'Save'}
                          </button>
                          {req.followUpDate && (
                            <button onClick={function() { handleClearFollowUp(req._id); setFollowUpOpen(null); }}
                              style={{ padding: '8px 14px', background: '#fff', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Session History */}
                  {expandedRequest === req._id && (
                    <div style={s.sessionHistory}>
                      <div style={s.sessionHistoryTitle}>Session History</div>

                      {/* Transfer Log Timeline */}
                      {transferLogs[req._id] && transferLogs[req._id].length > 0 && (
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>
                          <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Transfer History</div>
                          {transferLogs[req._id].map(function(log, i) {
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                                <div style={s.healerSegmentHeader}>
                                  <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#fff' }}>
                                    Sessions 1–{log.sessions_completed_before_transfer} | {log.from_healer_id ? log.from_healer_id.firstName + ' ' + log.from_healer_id.lastName : ''}
                                  </span>
                                  <span style={{ ...s.badge, background: '#fef9e7', color: '#d4ac0d' }}>Transferred</span>
                                </div>
                                <div style={{ fontSize: '11px', color: '#666' }}>
                                  → {log.to_healer_id ? log.to_healer_id.firstName + ' ' + log.to_healer_id.lastName : ''} (from session #{log.session_handover_number})
                                  <span style={{ marginLeft: '8px', color: '#999' }}>{new Date(log.transferred_on).toLocaleDateString('en-IN')}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {!req.sessions || req.sessions.length === 0 ? (
                        <div style={{ padding: '16px', color: '#999', fontSize: '13px' }}>No sessions recorded yet.</div>
                      ) : req.sessions.map(function(session, index) {
                        return (
                          <div key={index} style={s.sessionItem}>
                            <div style={s.sessionLeft}>
                              <div style={s.sessionNum}>#{index + 1}</div>
                              <div>
                                <div style={s.sessionDate}>{new Date(session.date).toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</div>
                                <div style={s.sessionDuration}>{session.duration} mins</div>
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              {(session.session_notes_internal || session.healerRemarks) && (
                                <div style={{ background: '#fef9e7', borderLeft: '3px solid #d4ac0d', padding: '6px 10px', borderRadius: '4px', marginBottom: '6px' }}>
                                  <div style={{ fontSize: '9px', fontWeight: '700', color: '#7D6608', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Internal Remarks</div>
                                  <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{session.session_notes_internal || session.healerRemarks}</div>
                                </div>
                              )}
                              {session.session_notes_patient && (
                                <div style={{ background: '#eafaf1', borderLeft: '3px solid #27ae60', padding: '6px 10px', borderRadius: '4px' }}>
                                  <div style={{ fontSize: '9px', fontWeight: '700', color: '#1a5c38', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Patient Notes</div>
                                  <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{session.session_notes_patient}</div>
                                </div>
                              )}
                              {!session.session_notes_internal && !session.healerRemarks && !session.session_notes_patient && (
                                <div style={{ fontSize: '13px', color: '#999' }}>No remarks</div>
                              )}
                              {session.scanningResults && session.scanningResults.length > 0 && (function() {
                                var scanKey = req._id + '_' + index;
                                var isOpen = !!expandedScanSessions[scanKey];
                                var groups = { major: [], minor: [], organs: [] };
                                session.scanningResults.forEach(function(r) { if (groups[r.group]) groups[r.group].push(r); });
                                var ENERGY_COLOR = { C: '#e67e22', D: '#7f8c8d', N: '#27ae60' };
                                var ACTIV_COLOR  = { U: '#2980b9', O: '#e74c3c', N: '#27ae60' };
                                var GROUP_LABELS = { major: 'Major Chakras', minor: 'Minor Chakras', organs: 'Organs & Mini' };
                                return (
                                  <div style={{ marginTop: '8px', border: '1px solid #e0f0ec', borderRadius: '6px', overflow: 'hidden' }}>
                                    <button type="button" onClick={function() { setExpandedScanSessions(function(prev) { return Object.assign({}, prev, { [scanKey]: !prev[scanKey] }); }); }}
                                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', background: '#1a6b5a', border: 'none', cursor: 'pointer' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#fff', fontFamily: "'Raleway', sans-serif", letterSpacing: '0.5px' }}>Chakra Scanning Results</span>
                                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>{session.scanningResults.length} readings</span>
                                      </div>
                                      <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>{isOpen ? '▲' : '▼'}</span>
                                    </button>
                                    {isOpen && (
                                      <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '480px' }}>
                                          <thead>
                                            <tr style={{ background: '#f5f8f7' }}>
                                              <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.8px', borderBottom: '1px solid #e0e0e0' }}>Chakra</th>
                                              <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '700', color: '#e67e22', fontSize: '10px', borderBottom: '1px solid #e0e0e0' }}>Energy Before</th>
                                              <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '700', color: '#27ae60', fontSize: '10px', borderBottom: '1px solid #e0e0e0' }}>Energy After</th>
                                              <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '700', color: '#2980b9', fontSize: '10px', borderBottom: '1px solid #e0e0e0' }}>Activation Before</th>
                                              <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: '700', color: '#8e44ad', fontSize: '10px', borderBottom: '1px solid #e0e0e0' }}>Activation After</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {['major', 'minor', 'organs'].map(function(gk) {
                                              if (!groups[gk] || groups[gk].length === 0) return null;
                                              return [
                                                <tr key={gk + '_header'}>
                                                  <td colSpan="5" style={{ padding: '5px 10px', background: '#eef7f5', fontSize: '10px', fontWeight: '700', color: '#1a6b5a', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{GROUP_LABELS[gk]}</td>
                                                </tr>
                                              ].concat(groups[gk].map(function(r, ri) {
                                                function pill(val, colorMap) {
                                                  if (!val) return <span style={{ color: '#ccc', fontSize: '11px' }}>—</span>;
                                                  return <span style={{ display: 'inline-block', width: '24px', height: '24px', lineHeight: '24px', borderRadius: '50%', background: colorMap[val] || '#ccc', color: '#fff', fontSize: '11px', fontWeight: '700', textAlign: 'center' }}>{val}</span>;
                                                }
                                                return (
                                                  <tr key={gk + ri} style={{ background: ri % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '6px 10px', color: '#333', fontWeight: '500' }}>{r.chakra}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{pill(r.energyBefore, ENERGY_COLOR)}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{pill(r.energyAfter, ENERGY_COLOR)}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{pill(r.activationBefore, ACTIV_COLOR)}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{pill(r.activationAfter, ACTIV_COLOR)}</td>
                                                  </tr>
                                                );
                                              }));
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                            <span style={{ ...s.badge, background: session.status === 'completed' ? '#eafaf1' : '#fef9e7', color: session.status === 'completed' ? '#27ae60' : '#d4ac0d' }}>{session.status}</span>
                            {/* Per-session feedback */}
                            {session.status === 'completed' && (function() {
                              var fbRequests = session.feedbackRequests || [];
                              var hasPending = fbRequests.some(function(fr) { return fr.status === 'pending'; });
                              var submitted = fbRequests.filter(function(fr) { return fr.status === 'submitted'; });
                              var sessionDateStr = new Date(session.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                              return (
                                <div style={{ marginTop: '8px' }}>
                                  {!hasPending && (
                                    <button onClick={function() { handleRequestSessionFeedback(req._id, index, sessionDateStr, req.patient ? req.patient.firstName : req.manualPatientName || ''); }}
                                      style={{ fontSize: '11px', padding: '4px 12px', background: '#2980b9', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontWeight: '700', letterSpacing: '0.5px' }}>
                                      Request Feedback
                                    </button>
                                  )}
                                  {hasPending && (
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontSize: '11px', color: '#d4ac0d', fontWeight: '700' }}>Feedback Pending</span>
                                      <button onClick={function() { handleRecallSessionFeedback(req._id, index, sessionDateStr); }}
                                        style={{ fontSize: '10px', padding: '3px 8px', background: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: '4px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontWeight: '700' }}>
                                        Recall
                                      </button>
                                    </div>
                                  )}
                                  {submitted.map(function(fr, fi) {
                                    return (
                                      <div key={fi} style={{ marginTop: '6px', background: '#eafaf1', borderLeft: '3px solid #27ae60', borderRadius: '4px', padding: '6px 10px' }}>
                                        <div style={{ fontSize: '9px', fontWeight: '700', color: '#1a5c38', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Patient Feedback</div>
                                        <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.5' }}>{fr.submittedText}</div>
                                        <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>{new Date(fr.submittedAt).toLocaleDateString('en-IN')} — requested by {fr.requestedByName}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  )}


                  {/* Transfer Log Panel (#19) */}
                  {transferPanelOpen === req._id && transferLogs[req._id] && (
                    <div style={{ marginTop: '12px', border: '1px solid #f0c040', borderRadius: '8px', overflow: 'hidden' }}>
                      <div style={{ background: '#fef9e7', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f0c040' }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#7D6608' }}>Healer Transfer History</span>
                        <span style={{ background: '#d4ac0d', color: '#fff', borderRadius: '10px', padding: '1px 8px', fontSize: '10px', fontWeight: '700' }}>{transferLogs[req._id].length}</span>
                      </div>
                      {transferLogs[req._id].map(function(log, i) {
                        return (
                          <div key={i} style={{ padding: '12px 16px', borderBottom: i < transferLogs[req._id].length - 1 ? '1px solid #f5f5f5' : 'none', background: '#fff' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>
                                {log.from_healer_id ? log.from_healer_id.firstName + ' ' + log.from_healer_id.lastName : '—'}
                                <span style={{ color: '#d4ac0d', margin: '0 8px' }}>→</span>
                                {log.to_healer_id ? log.to_healer_id.firstName + ' ' + log.to_healer_id.lastName : '—'}
                              </div>
                              <span style={{ fontSize: '11px', color: '#999' }}>{new Date(log.transferred_on).toLocaleDateString('en-IN')}</span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              Sessions 1–{log.sessions_completed_before_transfer} with previous healer · Handover from session #{log.session_handover_number}
                              {log.new_sessions_assigned ? ' · ' + log.new_sessions_assigned + ' sessions assigned' : ''}
                            </div>
                            {log.transfer_reason && (
                              <div style={{ marginTop: '6px', fontSize: '12px', color: '#7D6608', background: '#fef9e7', borderLeft: '3px solid #d4ac0d', padding: '5px 10px', borderRadius: '4px' }}>
                                Reason: {log.transfer_reason}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Case Notes Panel (#8) */}
                  {notesOpen === req._id && (
                    <div style={{ marginTop: '12px', border: '1px solid #bbd6f5', borderRadius: '8px', overflow: 'hidden' }}>
                      <div style={{ background: '#eaf4fb', padding: '10px 16px', borderBottom: '1px solid #bbd6f5' }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#1a5276' }}>Internal Case Notes</span>
                        <span style={{ fontSize: '10px', color: '#5d8aa8', marginLeft: '8px' }}>Visible to admin and healer only</span>
                      </div>
                      <div style={{ background: '#fff', maxHeight: '260px', overflowY: 'auto' }}>
                        {(caseNotesMap[req._id] || req.caseNotes || []).length === 0 ? (
                          <div style={{ padding: '16px', fontSize: '13px', color: '#bbb', textAlign: 'center' }}>No notes yet</div>
                        ) : (caseNotesMap[req._id] || req.caseNotes || []).map(function(note, ni) {
                          return (
                            <div key={ni} style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '11px', fontWeight: '700', color: note.authorRole === 'admin' ? '#4a8f82' : '#2980b9' }}>
                                  {note.authorName} <span style={{ fontWeight: '400', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>({note.authorRole})</span>
                                </span>
                                <span style={{ fontSize: '11px', color: '#bbb' }}>{new Date(note.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                              <div style={{ fontSize: '13px', color: '#333', lineHeight: '1.6' }}>{note.text}</div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ padding: '10px 16px', background: '#f9faf7', borderTop: '1px solid #e0e0e0', display: 'flex', gap: '8px' }}>
                        <input
                          value={noteText}
                          onChange={function(e) { setNoteText(e.target.value); }}
                          onKeyDown={function(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(req._id); } }}
                          placeholder="Add an internal note... (Enter to save)"
                          style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '13px', fontFamily: "'Open Sans', sans-serif" }}
                        />
                        <button onClick={function() { handleAddNote(req._id); }} disabled={savingNote || !noteText.trim()}
                          style={{ padding: '8px 16px', background: '#2980b9', color: '#fff', border: 'none', borderRadius: '5px', fontWeight: '700', fontSize: '12px', cursor: 'pointer', opacity: savingNote || !noteText.trim() ? 0.6 : 1 }}>
                          {savingNote ? '...' : 'Add'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Activities Panel */}
                  {activitiesOpen === req._id && (
                    <div style={{ borderTop: '2px solid #8e44ad', marginTop: '4px' }}>
                      <div style={{ padding: '10px 16px', background: '#f8f0ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: '700', fontSize: '12px', color: '#8e44ad', textTransform: 'uppercase', letterSpacing: '1px' }}>
                          Patient Activities
                        </div>
                        <span style={{ fontSize: '10px', color: '#8e44ad' }}>Visible to patient, healer &amp; admin</span>
                      </div>
                      <div style={{ padding: '0 16px 16px' }}>
                        <ActivityPanel
                          requestId={req._id}
                          activities={activitiesMap[req._id] || req.activities || []}
                          onUpdate={function(updated) {
                            setActivitiesMap(function(prev) { return Object.assign({}, prev, { [req._id]: updated }); });
                          }}
                        />
                      </div>
                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
 
const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
  main: { marginLeft: '260px', flex: 1, padding: '32px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' },
  headerTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '26px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px' },
  headerSub: { fontSize: '13px', color: '#666', margin: 0 },
  headerStats: { display: 'flex', gap: '24px' },
  headerStat: { textAlign: 'center' },
  headerStatNum: { fontFamily: "'Raleway', sans-serif", fontSize: '28px', fontWeight: '700', lineHeight: 1 },
  headerStatLabel: { fontSize: '11px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modalCard: { background: '#fff', borderRadius: '10px', padding: '36px', width: '100%', maxWidth: '520px' },
  modalTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '700', color: '#1a1a2e', marginBottom: '16px' },
  modalInfo: { background: '#f9faf7', borderRadius: '6px', padding: '14px 16px', marginBottom: '20px' },
  modalInfoName: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  modalInfoSub: { fontSize: '12px', color: '#666' },
  modalBtns: { display: 'flex', gap: '12px', marginTop: '20px' },
  tabs: { display: 'flex', gap: '4px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', marginBottom: '24px', width: 'fit-content' },
  tab: { padding: '10px 28px', background: 'transparent', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#666', cursor: 'pointer' },
  tabActive: { background: '#4a8f82', color: '#fff' },
  loading: { textAlign: 'center', padding: '60px', color: '#666', fontSize: '14px' },
  emptyCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '60px', textAlign: 'center' },
  emptyTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '18px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' },
  emptySub: { fontSize: '14px', color: '#999' },
  requestList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  requestCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px' },
  requestHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  requestHeaderLeft: { display: 'flex', gap: '14px', alignItems: 'flex-start' },
  requestHeaderRight: { textAlign: 'right' },
  patientAvatar: { width: '44px', height: '44px', borderRadius: '50%', background: 'linear-gradient(135deg, #4a8f82, #5a9a6e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#fff', flexShrink: 0 },
  patientName: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '3px' },
  patientContact: { fontSize: '12px', color: '#666', marginBottom: '4px' },
  requestCondition: { fontSize: '13px', color: '#4a8f82', fontWeight: '600' },
  beneficiaryTag: { marginLeft: '8px', fontSize: '11px', color: '#c9a84c', fontStyle: 'italic', fontWeight: '600' },
  badgeRow: { display: 'flex', gap: '6px', justifyContent: 'flex-end', marginBottom: '6px', flexWrap: 'wrap' },
  requestDate: { fontSize: '12px', color: '#999' },
  requestDesc: { fontSize: '13px', color: '#666', lineHeight: '1.7', padding: '12px 16px', background: '#f9faf7', borderRadius: '6px', marginBottom: '16px' },
  infoRow: { display: 'flex', gap: '12px', marginBottom: '16px' },
  infoBox: { background: '#f9faf7', borderRadius: '6px', padding: '10px 14px', flex: 1 },
  infoLabel: { fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#999', marginBottom: '4px', fontFamily: "'Raleway', sans-serif" },
  infoValue: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e' },
  actionRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  assignBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '9px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  reassignBtn: { background: 'transparent', color: '#4a8f82', border: '1px solid #4a8f82', borderRadius: '4px', padding: '9px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  expandBtn: { background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '9px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer' },
  sessionHistory: { marginTop: '16px', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' },
  sessionHistoryTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', padding: '12px 16px', background: '#f9faf7', borderBottom: '1px solid #e0e0e0' },
  sessionItem: { display: 'grid', gridTemplateColumns: '140px 1fr auto', gap: '16px', alignItems: 'start', padding: '14px 16px', borderBottom: '1px solid #f5f5f5' },
  sessionLeft: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  sessionNum: { width: '24px', height: '24px', borderRadius: '50%', background: '#4a8f82', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', flexShrink: 0 },
  sessionDate: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '3px' },
  sessionDuration: { fontSize: '11px', color: '#999' },
  sessionRemarks: { fontSize: '13px', color: '#555', lineHeight: '1.6' },
  badge: { fontFamily: "'Raleway', sans-serif", fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '20px', whiteSpace: 'nowrap' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '6px', fontFamily: "'Raleway', sans-serif" },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333' },
  submitBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '12px 28px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  cancelBtn: { background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '12px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  reopenBtn: { background: 'transparent', color: '#2980b9', border: '1px solid #2980b9', borderRadius: '4px', padding: '9px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  sessionSummaryRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', background: '#f9faf7', borderRadius: '6px', padding: '16px', marginBottom: '16px' },
  sessionSummaryBox: { textAlign: 'center' },
  sessionSummaryNum: { fontFamily: "'Raleway', sans-serif", fontSize: '22px', fontWeight: '700', color: '#4a8f82', lineHeight: 1 },
  sessionSummaryLabel: { fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#999', marginTop: '4px', fontFamily: "'Raleway', sans-serif" },
  healerSegmentHeader: { background: 'linear-gradient(135deg, #071e12, #0c2617)', borderRadius: '6px', padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1 },
  returnedInfoBox: { background: '#fdf0f0', border: '1px solid #e74c3c', borderRadius: '6px', padding: '14px 16px', marginBottom: '16px' },
  returnedBadge: { background: '#fdf0f0', color: '#e74c3c', border: '1px solid #e74c3c', fontFamily: "'Raleway', sans-serif", fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '20px' },
  dropBtn: { background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', padding: '9px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' }
};
 
export default HealingRequests;